import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';
interface GetInternalLinkFunction {
  ({
    codename,
    id,
    page,
  }: {
    codename?: string;
    id?: string;
    page?: Queries.VersionHomepageFragment | Queries.PageTreeItemFragment | Queries.PageFragment;
  }): string;
}

const useGlobalData = () => {
  const { allKenticoLink, kontentItemRoot, site, versions } =
    useStaticQuery<Queries.NavigationDataQuery>(graphql`
      query NavigationData {
        allKenticoLink {
          nodes {
            codename
            pageId
            url
          }
        }
        kontentItemRoot {
          ...RootData
          value {
            subpages {
              value {
                ...VersionHomepage
              }
            }
          }
        }
        versions {
          latestVersion
          sortedVersions
        }

        site {
          siteMetadata {
            isPreview
          }
        }
      }
    `);

  const { latestVersion, sortedVersions } = versions!;
  // NOTE internalLinks = [{ codename: 'docs_8_5', id: '8ea97ebz6', url: '/8.5' }, {etc...}]
  const internalLinks = allKenticoLink.nodes;
  const isPreview = site!.siteMetadata!.isPreview;
  const versionHomepageList = kontentItemRoot!.value.subpages.value;
  // NOTE only one LTS version is expected, hence usage of find()
  const ltsPage = versionHomepageList.find((homepage) => homepage.value.is_lts?.value === 'yes');
  const ltsVersion = ltsPage?.value.version_number.value;
  const {
    community_url,
    footer_content,
    logo,
    logo_title,
    meta_site_name,
    meta_site_url,
    page_not_found_content,
    page_not_found_meta_description,
    page_not_found_meta_title,
    page_not_found_title,
    product_news_url,
    search_placeholder,
    social_url,
    website_url,
  } = kontentItemRoot!.value;

  return useMemo(() => {
    const getHomepageByVersion = (version: string | undefined) => {
      const versionToFind = version || latestVersion;

      return versionHomepageList.find(
        (homepage) => homepage.value.version_number.value === versionToFind
      );
    };

    const getInternalLink: GetInternalLinkFunction = ({ codename, id, page }) => {
      const { codename: codenameToFind, id: idToFind } = page?.system || { id, codename };
      const hasCodenameToFind = Boolean(codenameToFind);
      const hasIdToFind = Boolean(idToFind);

      // NOTE Up to 2 links can be found for a page, a 'normal' and a 'latest'.
      // Normal: /9.6/my-page, Latest: /latest/my-page
      const matchingLinks = internalLinks.filter((link) => {
        if (hasCodenameToFind && link.codename === codenameToFind) {
          return true;
        }
        if (hasIdToFind && link.pageId === idToFind) {
          return true;
        }

        return false;
      });

      const latestLink = matchingLinks.find((link) => link.url.includes('/latest'));

      // NOTE Use the '/latest' link when available
      const internalLink = latestLink || matchingLinks[0];
      const isBrokenLink = internalLink?.url === undefined;

      return isBrokenLink ? '/latest' : internalLink.url;
    };

    return {
      /* Root Data */
      footerContent: footer_content!,
      logo: {
        description: logo.value.description!,
        title: logo_title!.value!,
        url: logo.value.url,
      },
      menuFooterUrls: {
        website: website_url!.value!,
        community: community_url!.value!,
        social: social_url!.value!,
        productNews: product_news_url!.value!,
      },
      meta: {
        siteName: meta_site_name.value,
        siteUrl: meta_site_url.value,
      },
      pageNotFoundData: {
        content: page_not_found_content?.value,
        metaDescription: page_not_found_meta_description?.value,
        metaTitle: page_not_found_meta_title?.value,
        title: page_not_found_title?.value,
      },
      searchPlaceholder: search_placeholder!.value!,

      /* Links, Versions */
      isPreview,
      latestVersion,
      ltsVersion,
      sortedVersions,
      versionHomepageList,
      getHomepageByVersion,
      getInternalLink,
    };
  }, [
    community_url,
    footer_content,
    internalLinks,
    isPreview,
    latestVersion,
    logo.value.description,
    logo.value.url,
    logo_title,
    ltsVersion,
    meta_site_name.value,
    meta_site_url.value,
    page_not_found_content,
    page_not_found_meta_description,
    page_not_found_meta_title,
    page_not_found_title,
    product_news_url,
    search_placeholder,
    social_url,
    sortedVersions,
    versionHomepageList,
    website_url,
  ]);
};

export const fragment = graphql`
  fragment RootData on kontent_item_root {
    value {
      community_url {
        value
      }
      footer_content {
        images {
          description
          height
          url
          image_id
          width
        }
        links {
          url_slug
          type
          link_id
          codename
        }
        value
      }
      logo {
        value {
          url
          description
        }
      }
      logo_title {
        value
      }
      meta_site_name {
        value
      }
      meta_site_url {
        value
      }
      page_not_found_content {
        value
      }
      page_not_found_meta_description {
        value
      }
      page_not_found_meta_title {
        value
      }
      page_not_found_title {
        value
      }
      product_news_url {
        value
      }
      search_placeholder {
        value
      }
      social_url {
        value
      }
      website_url {
        value
      }
    }
  }

  fragment VersionHomepage on kontent_item_version_homepage {
    value {
      subpages {
        value {
          __typename
          ...PageTreeItemRecursive
          ...Page
        }
      }
      is_lts {
        value
      }
      version_number {
        value
      }
    }
    system {
      id
      codename
      type
    }
  }

  fragment PageTreeItemRecursive on kontent_item_page_tree_item {
    ...PageTreeItem
    elements {
      subpages {
        value {
          ...Page
          ... on kontent_item_page_tree_item {
            ...PageTreeItem
            elements {
              subpages {
                value {
                  ...Page
                  ... on kontent_item_page_tree_item {
                    ...PageTreeItem
                    elements {
                      subpages {
                        value {
                          ...Page
                          ... on kontent_item_page_tree_item {
                            ...PageTreeItem
                            elements {
                              subpages {
                                value {
                                  ...Page
                                  ... on kontent_item_page_tree_item {
                                    ...PageTreeItem
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment Page on kontent_item_page {
    __typename
    elements {
      short_title {
        value
      }
      title {
        value
      }
      url_slug {
        value
      }
    }
    system {
      codename
      id
      type
    }
  }

  fragment PageTreeItem on kontent_item_page_tree_item {
    __typename
    elements {
      title {
        value
      }
      url_slug {
        value
      }
    }
    system {
      codename
      id
      type
    }
  }
`;

export default useGlobalData;
